import { forwardRef, useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { cn } from '../ui/utils';

interface PercentInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /**
   * Controlled value (as number, 0-100)
   */
  value?: number;
  /**
   * Change handler (receives number)
   */
  onChange?: (value: number) => void;
  /**
   * Minimum value
   * @default 0
   */
  min?: number;
  /**
   * Maximum value
   * @default 100
   */
  max?: number;
  /**
   * Number of decimal places
   * @default 2
   */
  decimals?: number;
  /**
   * Test ID for testing
   * @default 'percent-input'
   */
  testId?: string;
}

/**
 * PercentInput - Percentage input with automatic formatting
 * 
 * Formats input as percentage while typing.
 * Value is controlled as a number (0-100) for easy form handling.
 * 
 * @example
 * ```tsx
 * const [rate, setRate] = useState(5.5);
 * 
 * <PercentInput
 *   value={rate}
 *   onChange={setRate}
 *   placeholder="0,00%"
 *   decimals={2}
 * />
 * ```
 */
export const PercentInput = forwardRef<HTMLInputElement, PercentInputProps>(
  function PercentInput(
    {
      value = 0,
      onChange,
      min = 0,
      max = 100,
      decimals = 2,
      className,
      testId = 'percent-input',
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Update display value when value prop changes
    useEffect(() => {
      if (!isFocused) {
        setDisplayValue(value === 0 ? '' : value.toFixed(decimals) + '%');
      }
    }, [value, decimals, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Remove non-numeric characters except decimal separators and minus
      const cleaned = input.replace(/[^\d,.-]/g, '');

      // Parse to number
      const numericValue = parseFloat(cleaned.replace(',', '.')) || 0;

      // Clamp value between min and max
      const clampedValue = Math.min(Math.max(numericValue, min), max);

      // Update display value
      setDisplayValue(cleaned);

      // Call onChange with clamped value
      if (onChange) {
        onChange(clampedValue);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Show raw value when focused
      if (value === 0) {
        setDisplayValue('');
      } else {
        setDisplayValue(value.toString().replace('.', ','));
      }
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // Format with % when blurred
      if (value !== 0) {
        setDisplayValue(value.toFixed(decimals) + '%');
      }
      onBlur?.(e);
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn('font-mono tabular-nums', className)}
        data-testid={testId}
        aria-label="Valor percentual"
        {...props}
      />
    );
  }
);
